import { useEffect, useState } from "react";
import {
  createExternalAssessment,
  getExternalAssessments,
  updateExternalAssessment,
  voidExternalAssessment,
} from "../lib/api";
import {
  CATEGORY_OPTIONS,
  EXTERNAL_ASSESSMENT_NOTICE,
  RESULT_TYPE_OPTIONS,
  SOURCE_OPTIONS,
  categoryLabel,
  emptyForm,
  formFromRecord,
  formatAuditDate,
  formatResult,
  payloadFromForm,
  sourceLabel,
  statusLabel,
} from "../lib/externalAssessments";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ClipboardList, Loader2, Plus } from "lucide-react";

function FieldSelect({ id, value, onChange, options, disabled }) {
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export default function ExternalAssessmentsSection({ patientId, archived = false }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(patientId));
  const [saving, setSaving] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [formError, setFormError] = useState(null);

  const readOnly = Boolean(archived);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getExternalAssessments(patientId);
        if (cancelled) return;
        setRecords(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setError("No se pudieron cargar las evaluaciones externas.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (patientId) load();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(patientId));
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setForm(formFromRecord(record));
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setFormError(null);
    const payload = payloadFromForm({ ...form, patient_id: patientId });
    if (!payload.assessment_name || !payload.assessment_date || payload.result_value === "" || payload.result_value == null) {
      setFormError("Complete nombre, fecha y resultado declarados por el médico.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { patient_id: _omit, ...update } = payload;
        const res = await updateExternalAssessment(editingId, update);
        setRecords((prev) => prev.map((r) => (r.id === editingId ? res.data : r)));
      } else {
        const res = await createExternalAssessment(payload);
        setRecords((prev) => [res.data, ...prev]);
      }
      closeForm();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "No se pudo guardar el registro.");
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async (id) => {
    if (!String(voidReason || "").trim()) {
      setFormError("Indique el motivo de anulación.");
      return;
    }
    setSaving(true);
    try {
      const res = await voidExternalAssessment(id, voidReason.trim());
      setRecords((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      setVoidingId(null);
      setVoidReason("");
      setFormError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "No se pudo anular el registro.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="external-assessments-section">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900">
            <ClipboardList className="h-4 w-4 text-sky-700" />
            Evaluaciones externas
          </CardTitle>
          {!readOnly && (
            <Button type="button" size="sm" onClick={openCreate} data-testid="external-assessment-create">
              <Plus className="h-4 w-4 mr-1" />
              Registrar evaluación
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed" data-testid="external-assessment-notice">
          {EXTERNAL_ASSESSMENT_NOTICE}
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="external-assessments-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando evaluaciones externas…
          </div>
        )}

        {error && (
          <p className="text-sm text-red-700" data-testid="external-assessments-error">
            {error}
          </p>
        )}

        {!loading && !error && records.length === 0 && (
          <p className="text-sm text-slate-600" data-testid="external-assessments-empty">
            No hay evaluaciones externas registradas. Puede anotar aquí un resultado obtenido fuera de Aurora.
          </p>
        )}

        {formError && (
          <p className="text-sm text-red-700" data-testid="external-assessment-form-error">
            {formError}
          </p>
        )}

        {formOpen && !readOnly && (
          <form className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3" onSubmit={handleSave} data-testid="external-assessment-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ea-category">Categoría</Label>
                <FieldSelect
                  id="ea-category"
                  value={form.category}
                  onChange={(v) => setField("category", v)}
                  options={CATEGORY_OPTIONS}
                />
              </div>
              <div>
                <Label htmlFor="ea-date">Fecha de realización</Label>
                <Input
                  id="ea-date"
                  type="date"
                  value={form.assessment_date}
                  onChange={(e) => setField("assessment_date", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ea-name">Nombre declarado</Label>
                <Input
                  id="ea-name"
                  value={form.assessment_name}
                  onChange={(e) => setField("assessment_name", e.target.value)}
                  placeholder="Ej. Evaluación funcional externa"
                />
              </div>
              <div>
                <Label htmlFor="ea-type">Tipo de resultado</Label>
                <FieldSelect
                  id="ea-type"
                  value={form.result_type}
                  onChange={(v) => setField("result_type", v)}
                  options={RESULT_TYPE_OPTIONS}
                />
              </div>
              <div>
                <Label htmlFor="ea-value">Resultado</Label>
                <Input
                  id="ea-value"
                  value={form.result_value}
                  onChange={(e) => setField("result_value", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ea-unit">Unidad (opcional)</Label>
                <Input
                  id="ea-unit"
                  value={form.result_unit}
                  onChange={(e) => setField("result_unit", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ea-source">Fuente</Label>
                <FieldSelect
                  id="ea-source"
                  value={form.source}
                  onChange={(v) => setField("source", v)}
                  options={SOURCE_OPTIONS}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ea-interp">Interpretación escrita por el médico</Label>
                <Textarea
                  id="ea-interp"
                  value={form.physician_interpretation}
                  onChange={(e) => setField("physician_interpretation", e.target.value)}
                  rows={3}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ea-source-note">Nota de fuente (opcional)</Label>
                <Input
                  id="ea-source-note"
                  value={form.source_note}
                  onChange={(e) => setField("source_note", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ea-notes">Notas (opcional)</Label>
                <Textarea id="ea-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" size="sm" disabled={saving} data-testid="external-assessment-save">
                {saving ? "Guardando…" : "Guardar registro"}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={closeForm}>
                Cancelar
              </Button>
            </div>
          </form>
        )}

        <ul className="space-y-3">
          {records.map((record) => {
            const voided = record.status === "voided";
            return (
              <li
                key={record.id}
                data-testid={`external-assessment-card-${record.id}`}
                className={`rounded-lg border p-3 ${voided ? "border-slate-200 bg-slate-100 opacity-80" : "border-slate-200 bg-white"}`}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium text-slate-900 break-words">{record.assessment_name}</p>
                    <p className="text-xs text-slate-500">
                      {record.assessment_date} · {categoryLabel(record.category)} · {sourceLabel(record.source)}
                    </p>
                    <p className="text-sm text-slate-800">Resultado: {formatResult(record)}</p>
                    {record.physician_interpretation ? (
                      <p className="text-sm text-slate-700">Interpretación del médico: {record.physician_interpretation}</p>
                    ) : null}
                    <p className="text-xs text-slate-500">
                      Fecha de registro: {formatAuditDate(record.created_at)}
                    </p>
                    {record.status === "corrected" || (record.updated_at && record.updated_at !== record.created_at) ? (
                      <p className="text-xs text-slate-500">
                        Última corrección: {formatAuditDate(record.updated_at)}
                      </p>
                    ) : null}
                  </div>
                  <Badge variant="outline" className={voided ? "border-slate-400 text-slate-600" : ""}>
                    {statusLabel(record.status)}
                  </Badge>
                </div>
                {!readOnly && !voided && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => openEdit(record)} data-testid={`external-assessment-edit-${record.id}`}>
                      Editar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setVoidingId(record.id);
                        setVoidReason("");
                      }}
                      data-testid={`external-assessment-void-${record.id}`}
                    >
                      Anular
                    </Button>
                  </div>
                )}
                {voidingId === record.id && (
                  <div className="mt-3 space-y-2" data-testid="external-assessment-void-confirm">
                    <Label htmlFor="ea-void-reason">Motivo de anulación</Label>
                    <Textarea
                      id="ea-void-reason"
                      value={voidReason}
                      onChange={(e) => setVoidReason(e.target.value)}
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button type="button" size="sm" disabled={saving} onClick={() => handleVoid(record.id)} data-testid="external-assessment-void-confirm-btn">
                        Confirmar anulación
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setVoidingId(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
